import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import FormPage from '../../components/ui/FormPage.tsx';

export type SettlementKind = 'payable' | 'receivable' | 'debt';

const kindConfig = {
  payable: {
    title: 'Pagar',
    subtitle: 'Registra saída no fluxo de caixa e atualiza o saldo a pagar',
    breadcrumb: 'Financeiro / Contas / A pagar / Baixa',
    backTo: '/financeiro/contas?tab=pagar',
    backLabel: 'Voltar às contas a pagar',
    cta: 'Confirmar pagamento',
    listKey: 'contas-a-pagar',
    fetchPath: (id: string) => `/financeiro/contas-a-pagar`,
    endpoint: (id: string) => `/financeiro/contas-a-pagar/${id}/baixa`,
    pick: (rows: any[], id: string) => rows.find((r: any) => String(r.id) === String(id)),
    counterparty: (row: any) => row?.creditor,
    amountField: (row: any) => Number(row?.remainingAmount || row?.amount || 0),
  },
  receivable: {
    title: 'Receber',
    subtitle: 'Registra entrada no fluxo de caixa e atualiza o saldo a receber',
    breadcrumb: 'Financeiro / Contas / A receber / Baixa',
    backTo: '/financeiro/contas?tab=receber',
    backLabel: 'Voltar às contas a receber',
    cta: 'Confirmar recebimento',
    listKey: 'contas-a-receber',
    fetchPath: (id: string) => `/financeiro/contas-a-receber`,
    endpoint: (id: string) => `/financeiro/contas-a-receber/${id}/baixa`,
    pick: (rows: any[], id: string) => rows.find((r: any) => String(r.id) === String(id)),
    counterparty: (row: any) => row?.customer,
    amountField: (row: any) => Number(row?.remainingAmount || row?.amount || 0),
  },
  debt: {
    title: 'Pagar parcela',
    subtitle: 'Baixa de dívida · registra saída no fluxo de caixa',
    breadcrumb: 'Financeiro / Dívidas / Baixa',
    backTo: '/financeiro/dividas',
    backLabel: 'Voltar às dívidas',
    cta: 'Confirmar pagamento',
    listKey: 'financeiro-dividas',
    fetchPath: (id: string) => `/financeiro/dividas`,
    endpoint: (id: string) => `/financeiro/dividas/${id}/baixa`,
    pick: (rows: any[], id: string) => rows.find((r: any) => String(r.id) === String(id)),
    counterparty: (row: any) => row?.creditor,
    amountField: (row: any) => Number(row?.remainingAmount || row?.amount || 0),
  },
};

interface Props {
  kind: SettlementKind;
}

const SettlementPage: React.FC<Props> = ({ kind }) => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cfg = kindConfig[kind];
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false); // sync guard — state alone loses race on double-click
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    paymentAmount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    cashAccountId: '',
    paymentMethod: 'pix',
    notes: '',
  });

  const { data: rows = [], isLoading } = useQuery(cfg.listKey, async () => {
    const res = await api.get(cfg.fetchPath(id));
    return res.data as any[];
  });

  const item = cfg.pick(rows, id);
  const amount = item ? cfg.amountField(item) : 0;

  useEffect(() => {
    if (item && !form.paymentAmount) {
      setForm(f => ({ ...f, paymentAmount: String(amount) }));
    }
  }, [item, amount, form.paymentAmount]);

  const { data: accounts } = useQuery('cash-accounts', async () => {
    const res = await api.get('/financeiro/contas-caixa');
    return res.data as Array<{ id: string; name: string; isDefault: boolean }>;
  });

  useEffect(() => {
    if (accounts?.length && !form.cashAccountId) {
      const def = accounts.find(a => a.isDefault) || accounts[0];
      setForm(f => ({ ...f, cashAccountId: String(def.id) }));
    }
  }, [accounts, form.cashAccountId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const paymentAmount = Number(String(form.paymentAmount).replace(',', '.'));
    if (!(paymentAmount > 0)) {
      setError('Informe um valor de pagamento válido.');
      return;
    }
    if (!form.paymentDate) {
      setError('Informe a data do pagamento.');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      await api.post(cfg.endpoint(id), {
        paymentAmount,
        amount: paymentAmount,
        paymentDate: form.paymentDate,
        date: form.paymentDate,
        cashAccountId: form.cashAccountId ? Number(form.cashAccountId) : undefined,
        paymentMethod: form.paymentMethod,
        notes: form.notes,
      });
      queryClient.invalidateQueries(cfg.listKey);
      queryClient.invalidateQueries('cash-flow');
      navigate(cfg.backTo);
    } catch (err: any) {
      submittingRef.current = false;
      setError(err?.response?.data?.error || 'Erro ao registrar a baixa.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!item) {
    return <div className="field-error">Registro não encontrado.</div>;
  }

  return (
    <FormPage
      backTo={cfg.backTo}
      backLabel={cfg.backLabel}
      breadcrumb={cfg.breadcrumb}
      title={cfg.title}
      subtitle={cfg.subtitle}
      hint={`${item.description}${cfg.counterparty(item) ? ` · ${cfg.counterparty(item)}` : ''} · ${formatCurrency(amount)}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(cfg.backTo)}>
            Cancelar
          </button>
          <button type="submit" form="settlement-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Confirmando…' : cfg.cta}
          </button>
        </>
      }
    >
      <form id="settlement-form" onSubmit={submit} noValidate>
        {error && <div className="field-error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="form-section-title">Confirmação</div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="paymentAmount">Valor *</label>
            <input
              id="paymentAmount"
              className="input"
              inputMode="decimal"
              value={form.paymentAmount}
              onChange={e => setForm({ ...form, paymentAmount: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="paymentDate">Data *</label>
            <input
              id="paymentDate"
              type="date"
              className="input"
              value={form.paymentDate}
              onChange={e => setForm({ ...form, paymentDate: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="cashAccountId">Conta</label>
            <select
              id="cashAccountId"
              className="select"
              value={form.cashAccountId}
              onChange={e => setForm({ ...form, cashAccountId: e.target.value })}
            >
              {(accounts || []).map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="paymentMethod">Forma</label>
            <select
              id="paymentMethod"
              className="select"
              value={form.paymentMethod}
              onChange={e => setForm({ ...form, paymentMethod: e.target.value })}
            >
              <option value="pix">PIX</option>
              <option value="ted">TED / Transferência</option>
              <option value="boleto">Boleto</option>
              <option value="cash">Dinheiro</option>
              <option value="card">Cartão</option>
            </select>
          </div>
          <div className="field full">
            <label htmlFor="notes">Observações</label>
            <input
              id="notes"
              className="input"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
      </form>
    </FormPage>
  );
};

export default SettlementPage;
